import { FileText } from 'lucide-react';
import { Button, IconTile, Pill } from '@/shared/components';
import type { InvoiceResponse } from '@/shared/api';
import { formatDate, formatMoney } from '@/shared/lib/format';
import { invoiceStatus } from './bookingInvoiceModel';

interface InvoiceDocumentItemProps {
  invoice: InvoiceResponse;
  kind: 'invoice' | 'cancellation' | 'correction';
  onDownload: (id: string, fileName: string) => void;
  onCancel?: () => void;
}

export function InvoiceDocumentItem({
  invoice,
  kind,
  onDownload,
  onCancel,
}: InvoiceDocumentItemProps) {
  const status =
    kind === 'cancellation'
      ? 'CreditNote'
      : kind === 'correction'
        ? 'Correction'
        : invoiceStatus(invoice);
  const statusPresentation = {
    Draft: { label: 'Entwurf', variant: 'default' as const },
    Open: { label: 'Offen', variant: 'warning' as const },
    Paid: { label: 'Bezahlt', variant: 'live' as const },
    Cancelled: {
      label: invoice.kind === 'DamageClaim' ? 'Aufgehoben' : 'Storniert',
      variant: 'stop' as const,
    },
    CreditNote: {
      label: invoice.kind === 'DamageClaim' ? 'Aufhebung' : 'Stornorechnung',
      variant: 'stop' as const,
    },
    Correction: { label: 'Korrektur', variant: 'warning' as const },
  }[status];

  return (
    <div
      id={`invoice-${invoice.id}`}
      className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 sm:flex"
    >
      <IconTile variant="muted" size="sm" className="shrink-0">
        <FileText size={14} strokeWidth={2} />
      </IconTile>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">
            {invoice.invoiceNumber ?? 'Ohne Nummer'}
          </span>
          <Pill variant={statusPresentation.variant} size="sm">
            {statusPresentation.label}
          </Pill>
        </div>
        <div className="text-ink-soft text-xs">
          {formatMoney(invoice.totalAmount, invoice.currency)} · Datum{' '}
          {formatDate(invoice.issuedOn)}
        </div>
      </div>
      <div className="col-span-2 flex shrink-0 justify-end gap-2 sm:col-span-1 sm:justify-start">
        {invoice.canAccessDocuments === false ? (
          <span className="text-ink-soft text-xs">Gastdatenzugriff fehlt</span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="border-line min-h-11 border-[1.5px] sm:min-h-9"
            aria-label={`PDF ${invoice.invoiceNumber ?? 'Entwurf'} herunterladen`}
            onClick={() => onDownload(invoice.id, invoice.fileName)}
          >
            PDF
          </Button>
        )}
        {invoice.canAccessDocuments !== false && onCancel && (
          <Button
            size="sm"
            variant="ghost"
            className="text-sealwax hover:bg-sealwax/10 border-sealwax/35 min-h-11 border-[1.5px] sm:min-h-9"
            aria-label={`${invoice.kind === 'DamageClaim' ? 'Schadensersatzforderung' : 'Rechnung'} ${invoice.invoiceNumber ?? 'ohne Nummer'} ${invoice.kind === 'DamageClaim' ? 'aufheben' : 'stornieren'}`}
            onClick={onCancel}
          >
            {invoice.kind === 'DamageClaim' ? 'Forderung aufheben' : 'Rechnung stornieren'}
          </Button>
        )}
      </div>
    </div>
  );
}
